import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useQueryParams } from "@/hooks/useQueryParam";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function ShareRedirect(): React.ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useQueryParams<{ t: string; entity: string; p: string }>();
  const [state, setState] = useState<"loading" | "brokenLink" | "wrongProject" | "wrongEntity">("loading");

  const entityType = params.t;
  const entityId = params.entity;
  const projectId = params.p;

  const { data: connectedProjects, isPending: isPendingConnectedProjects } = useLinkedProjects();

  useEffect(() => {
    void (() => {
      if (!entityType || !entityId || !projectId) {
        setState("brokenLink");

        return;
      }

      if (!connectedProjects) {
        return;
      }

      const project = connectedProjects.find((x) => x.id === projectId);
      if (!project) {
        setState("wrongProject");

        return;
      }

      const slug = project.slug;
      switch (entityType) {
        case "message":
          return navigate(routes.messageFeed.details({ slug, id: entityId }));
        case "ticket":
          return navigate(routes.tickets.details({ slug, id: entityId }));
        case "survey":
          return navigate(routes.surveys.details({ slug, id: entityId }));
        case "event":
          return navigate(routes.events.details({ slug, id: entityId }));
        case "poll":
          return navigate(routes.messageFeed.list({ slug }));
        case "service":
          return navigate(routes.servicePartners.details({ slug, id: entityId }));
        case "document":
          return navigate(routes.documents.documentsList({ slug, folderId: entityId }));
        case "group":
          return navigate(routes.interestGroups.details({ slug, id: entityId }));
        case "bookableAsset":
          return navigate(routes.bookings.bookAsset({ slug, aid: entityId }));
        default:
          setState("wrongEntity");
      }
    })();
  }, [entityType, entityId, navigate, connectedProjects, projectId]);

  if (isPendingConnectedProjects || state === "loading") {
    return null;
  }

  if (state === "brokenLink" || state === "wrongEntity") {
    return <ErrorPage status={404} />;
  }

  if (state === "wrongProject") {
    return <ErrorPage status={403} extraInfo={t("page.share-redirect.no-access-to-project")} />;
  }

  return null;
}
